import React from "react";
import LineChartGeneric from "../../../shared/components/generic/chart/line-chart/line-chart-generic";
import MonetaryUtils from "../../../shared/utils/MonetaryUtils";
import { hasValue } from "../../utils/pacto-rebates-utils";
import {
	clampChartDataValues,
	clampDailySalesChartDataValues,
	getDataMaxValue,
	getGoalBasedChartMaxValue,
	getGoalDiscountPercentage,
	getGoalMinimumValue,
	getGoalValues,
	getMiddleDate,
	getOriginalValue,
	normalizeChartDate,
	sortGoalsByMinimumValue,
	getCompactChartLayout
} from "../utils/dashboard-chart-utils";

const formatChartCurrency = value => {
	return MonetaryUtils.formatCurrency(value)
		.replace(/\s/g, "")
		.replace(",00", "");
};

const formatMonthlyPointValue = formatChartCurrency;
const formatDailyPointValue = formatChartCurrency;

const normalizeCycle = cycle => {
	const months = (cycle.months).map(month => ({
		...month,
		startDate: normalizeChartDate(month.startDate),
		endDate: normalizeChartDate(month.endDate)
	}));

	return {
		...cycle,
		startDate: normalizeChartDate(cycle.startDate) || months[0]?.startDate,
		endDate: normalizeChartDate(cycle.endDate) || months[months.length - 1]?.endDate,
		months
	};
};

const getXAxisFromCycle = cycle => {
	const monthTicks = cycle.months.map(month => getMiddleDate(month.startDate, month.endDate));

	const monthLabels = cycle.months.reduce((acc, month, index) => {
		acc[monthTicks[index]] = month.label;
		return acc;
	}, {});

	return {
		dataKey: "date",
		type: "number",
		scale: "time",
		domain: [cycle.startDate, cycle.endDate],
		ticks: monthTicks,
		tickFormatter: value => monthLabels[value] || "",
		orientation: "top",
		verticalGrid: false,
		padding: { left: 0, right: 0 },
		axisLine: { stroke: "#5aa1dd", strokeWidth: 2 },
		tick: { fill: "#00518b", fontSize: 13, fontWeight: 700 }
	};
};

const getMonthBoundaryReferenceLines = cycle => {
	return cycle.months.map(month => ({
		key: `month-end-${month.key}`,
		x: month.endDate,
		color: "#1f6f55",
		dash: "4 4",
		width: 2
	}));
};

const getCurrentDateReferenceLine = currentDate => ({
	key: "current-date",
	x: currentDate,
	color: "#00518b",
	width: 2
});

const isClosedCyclePoint = payload => {
	return payload?.type === "actual" || payload?.isClosedCyclePoint;
};

const addClosedCycleInfo = (data = [], currentDate) => {
	return data.map(item => ({
		...item,
		isClosedCyclePoint: Number(currentDate) >= Number(item?.date)
	}));
};

const normalizeChartData = (type, data = [], currentDate) => {
	const dataWithClosedCycleInfo = addClosedCycleInfo(data, currentDate);

	if (type !== "dailySales") {
		return dataWithClosedCycleInfo;
	}

	return dataWithClosedCycleInfo.flatMap(item => {
		const hasCurrentDailySales = hasValue(item.currentDailySales);
		const hasDailyAverage = hasValue(item.dailyAverage);

		if (!hasCurrentDailySales) {
			return [item];
		}

		const currentDailySalesItem = {
			...item,
			date: currentDate,
			dailyAverage: null,
			type: "current",
			isClosedCyclePoint: true
		};

		if (!hasDailyAverage) {
			return [currentDailySalesItem];
		}

		return [
			{ ...item, currentDailySales: null },
			currentDailySalesItem
		];
	});
};

const mainChartMargin = {
	top: 56,
	right: 88,
	bottom: 56,
	left: 104
};

const referenceLabelVerticalOffset = 0;

const closedCycleLegendKeyByType = {
	monthlySales: "actual",
	dailySales: "actual-daily-average",
	variluxMonthly: "actual-pairs-average"
};

const getMonthlyGoalReferenceLines = ({
	goals = [],
	valueFormatter = formatChartCurrency,
	labelOffset = 12,
	rightLabelOffset = 12
}) => {
	return goals.map(goal => {
		const minimumValue = getGoalMinimumValue(goal);
		const discountPercentage = getGoalDiscountPercentage(goal);

		return {
			key: `goal-${discountPercentage}`,
			y: Number(minimumValue || 0),
			color: "#00518b",
			dash: "4 3",
			width: 2,

			label: valueFormatter(minimumValue),
			labelTooltip: `Meta: ${valueFormatter(minimumValue)} | Desconto: ${MonetaryUtils.formatPercentage(discountPercentage)}`,
			labelColor: "#00518b",
			labelFontSize: 12,
			labelFontWeight: 700,
			labelOffset,
			labelVerticalOffset: referenceLabelVerticalOffset,

			rightLabel: MonetaryUtils.formatPercentage(discountPercentage),
			rightLabelTooltip: `Desconto: ${MonetaryUtils.formatPercentage(discountPercentage)} | Meta: ${valueFormatter(minimumValue)}`,
			rightLabelColor: "#00518b",
			rightLabelFontSize: 12,
			rightLabelFontWeight: 700,
			rightLabelOffset,
			rightLabelVerticalOffset: referenceLabelVerticalOffset
		};
	});
};

const getZeroDiscountReferenceLabel = ({
	baseline = 0,
	rightLabelOffset = 12
}) => ({
	key: "discount-0",
	y: Number(baseline || 0) / 2,
	color: "transparent",
	width: 0,
	rightLabel: "0%",
	rightLabelColor: "#00518b",
	rightLabelFontSize: 12,
	rightLabelFontWeight: 700,
	rightLabelOffset,
	rightLabelVerticalOffset: referenceLabelVerticalOffset
});

const getMonthlySalesConfig = (cycle, options = {}) => {
	const {
		goals = [],
		baseline = 35000,
		data = []
	} = options;

	const sortedGoals = sortGoalsByMinimumValue(goals);
	const goalValues = getGoalValues(sortedGoals);
	const dataMaxValue = getDataMaxValue(data, ["value"]);

	const chartMaxValue = getGoalBasedChartMaxValue({
		baseline,
		goalValues,
		dataMaxValue,
		roundBase: 1000
	});

	return {
		height: 600,
		margin: mainChartMargin,
		valueFormatter: formatChartCurrency,
		xAxis: getXAxisFromCycle(cycle),
		yAxisLeft: {
			domain: [0, chartMaxValue],
			ticks: [baseline, ...goalValues],
			tick: false,
			tickFormatter: () => ""
		},
		yAxisRight: {
			domain: [0, chartMaxValue],
			tick: false,
			tickFormatter: () => ""
		},
		areas: [
			{
				key: "below-baseline",
				y1: 0,
				y2: baseline,
				gradient: {
					id: "monthly-below-baseline-gradient",
					from: "#d9d9d9",
					to: "#ffffff"
				}
			},
			{
				key: "discount-gradient",
				y1: baseline,
				y2: chartMaxValue,
				gradient: {
					id: "monthly-sales-gradient",
					from: "#DCF4FF",
					to: "#007BD3"
				}
			}
		],
		referenceLines: [
			...getMonthBoundaryReferenceLines(cycle),
			{
				key: "baseline",
				y: baseline,
				color: "#c3311d",
				width: 2,
				label: formatChartCurrency(baseline),
				labelTooltip: `Baseline média mensal: ${formatChartCurrency(baseline)}`,
				labelTooltipClassName: "generic-line-chart__reference-tooltip--baseline",
				labelColor: "#c3311d",
				labelFontSize: 12,
				labelFontWeight: 700,
				labelOffset: 12
			},
			...getMonthlyGoalReferenceLines({
				goals: sortedGoals,
				valueFormatter: formatChartCurrency
			}),
			getZeroDiscountReferenceLabel({
				baseline
			})
		],
		series: [
			{
				key: "average",
				dataKey: "value",
				name: "Média acumulada",
				color: "#65ad3f",
				width: 3,
				showLabels: true,
				labelFormatter: (value, payload) => {
					return formatMonthlyPointValue(
						getOriginalValue(payload, "value", value)
					);
				},
				dot: payload => ({
					radius: 8,
					fill: isClosedCyclePoint(payload) ? "#65ad3f" : "#ffffff",
					stroke: "#65ad3f",
					strokeWidth: 3
				})
			}
		],
		legend: [
			{
				key: "actual",
				label: "Média acumulada nos fechamentos de mês",
				color: "#65ad3f",
				fill: "#65ad3f"
			},
			{
				key: "projection",
				label: "Projeção de média acumulada nos próximos fechamentos de mês",
				color: "#65ad3f",
				fill: "#ffffff"
			}
		]
	};
};

const getDailySalesConfig = (cycle, options = {}) => {
	const {
		goals = [],
		baseline = 1640,
		data = []
	} = options;

	const sortedGoals = sortGoalsByMinimumValue(goals);
	const goalValues = getGoalValues(sortedGoals);
	const dataMaxValue = getDataMaxValue(data, [
		"dailyAverage",
		"currentDailySales"
	]);

	const chartMaxValue = getGoalBasedChartMaxValue({
		baseline,
		goalValues,
		dataMaxValue,
		roundBase: 100,
	});

	return {
		...getMonthlySalesConfig(cycle, {
			goals: sortedGoals,
			baseline,
			data
		}),
		className: "dashboard__line-chart--daily",
		valueFormatter: formatDailyPointValue,
		yAxisLeft: {
			domain: [0, chartMaxValue],
			ticks: [baseline, ...goalValues],
			tick: false,
			tickFormatter: () => ""
		},
		yAxisRight: {
			domain: [0, chartMaxValue],
			tick: false,
			tickFormatter: () => ""
		},
		areas: [
			{
				key: "below-baseline",
				y1: 0,
				y2: baseline,
				gradient: {
					id: "daily-below-baseline-gradient",
					from: "#d9d9d9",
					to: "#ffffff"
				}
			},
			{
				key: "discount-gradient",
				y1: baseline,
				y2: chartMaxValue,
				gradient: {
					id: "daily-sales-gradient",
					from: "#DCF4FF",
					to: "#007BD3"
				}
			}
		],
		referenceLines: [
			...getMonthBoundaryReferenceLines(cycle),
			{
				key: "baseline",
				y: baseline,
				color: "#c3311d",
				width: 2,
				label: formatDailyPointValue(baseline),
				labelTooltip: `Baseline média diária: ${formatDailyPointValue(baseline)}`,
				labelTooltipClassName: "generic-line-chart__reference-tooltip--baseline",
				labelColor: "#c3311d",
				labelFontSize: 12,
				labelFontWeight: 700,
				labelOffset: 12
			},
			...getMonthlyGoalReferenceLines({
				goals: sortedGoals,
				valueFormatter: formatDailyPointValue
			}),
			getZeroDiscountReferenceLabel({
				baseline
			})
		],
		series: [
			{
				key: "current-daily-sales",
				dataKey: "currentDailySales",
				name: "Média de vendas diárias neste mês",
				color: "#5fa3df",
				width: 0,
				showLabels: true,
				labelFormatter: (value, payload) => {
					return formatDailyPointValue(
						getOriginalValue(payload, "currentDailySales", value)
					);
				},
				dot: () => ({
					radius: 7,
					fill: "#5fa3df",
					stroke: "#5fa3df",
					strokeWidth: 2
				})
			},
			{
				key: "daily-average",
				dataKey: "dailyAverage",
				name: "Média diária acumulada",
				color: "#65ad3f",
				width: 3,
				showLabels: true,
				connectNulls: true,
				labelFormatter: (value, payload) => {
					return formatDailyPointValue(
						getOriginalValue(payload, "dailyAverage", value)
					);
				},
				dot: payload => ({
					radius: 8,
					fill: isClosedCyclePoint(payload) ? "#65ad3f" : "#ffffff",
					stroke: "#65ad3f",
					strokeWidth: 3
				})
			}
		],
		legend: [
			{
				key: "current-daily-sales",
				label: "Média de vendas diárias neste mês",
				color: "#5fa3df",
				fill: "#5fa3df"
			},
			{
				key: "actual-daily-average",
				label: "Média diária acumulada nos fechamentos de mês",
				color: "#65ad3f",
				fill: "#65ad3f"
			},
			{
				key: "projected-daily-average",
				label: "Projeção de média diária acumulada nos próximos fechamentos de mês",
				color: "#65ad3f",
				fill: "#ffffff"
			}
		]
	};
};

const getVariluxMonthlyConfig = (cycle, options = {}) => {
	const {
		baseline = 0,
		goal = 0,
		data = []
	} = options;

	const dataMaxValue = getDataMaxValue(data, ["pairsAverage"]);

	const referenceMaxValue = Math.max(
		Number(baseline || 0),
		Number(goal || 0),
		dataMaxValue,
		0
	);

	const chartMaxValue = Math.max(
		Math.ceil(referenceMaxValue * 1.2),
		10
	);

	return {
		height: 320,
		margin: {
			...mainChartMargin,
			bottom: 56
		},
		valueFormatter: value => value,
		xAxis: getXAxisFromCycle(cycle),
		yAxisLeft: {
			domain: [0, chartMaxValue],
			ticks: [baseline, goal].filter(Boolean),
			tick: false,
			tickFormatter: () => ""
		},
		areas: [
			{
				key: "below-baseline",
				y1: 0,
				y2: baseline,
				gradient: {
					id: "varilux-below-baseline-gradient",
					from: "#d9d9d9",
					to: "#ffffff"
				}
			},
			{
				key: "varilux-gradient",
				y1: baseline,
				y2: chartMaxValue,
				gradient: {
					id: "varilux-monthly-gradient",
					from: "#CFF8F0",
					to: "#44C2A7"
				}
			}
		],
		referenceLines: [
			...getMonthBoundaryReferenceLines(cycle),
			{
				key: "baseline-varilux",
				y: Number(baseline || 0),
				color: "#c3311d",
				width: 2,
				label: `${baseline} pares`,
				labelColor: "#c3311d",
				labelFontSize: 12,
				labelFontWeight: 700,
				labelOffset: 12
			},
			{
				key: "goal-varilux",
				y: Number(goal || 0),
				color: "#00518b",
				dash: "4 3",
				width: 2,
				label: `${goal} pares`,
				labelColor: "#00518b",
				labelFontSize: 12,
				labelFontWeight: 700,
				labelOffset: 12
			}
		],
		series: [
			{
				key: "pairs-average",
				dataKey: "pairsAverage",
				name: "Média acumulada de pares",
				color: "#65ad3f",
				width: 3,
				showLabels: true,
				labelFormatter: value => value,
				dot: payload => ({
					radius: 8,
					fill: isClosedCyclePoint(payload) ? "#65ad3f" : "#ffffff",
					stroke: "#65ad3f",
					strokeWidth: 3
				})
			}
		],
		legend: [
			{
				key: "actual-pairs-average",
				label: "Média acumulada de pares nos fechamentos de mês",
				color: "#65ad3f",
				fill: "#65ad3f"
			},
			{
				key: "projected-pairs-average",
				label: "Projeção de média acumulada de pares nos próximos fechamentos de mês",
				color: "#65ad3f",
				fill: "#ffffff"
			}
		]
	};
};

const DashboardLineChart = ({
	type,
	data = [],
	simulatedToday,
	cycle,
	isClosedCycle = false,
	goals = [],
	baseline,
	variluxGoal,
	variluxBaseline,
	className = ""
}) => {
	if (!cycle || !data.length) {
		return null;
	}

	const currentDate = simulatedToday;
	const normalizedCycle = normalizeCycle(cycle);
	const normalizedData = normalizeChartData(type, data, currentDate);

	const chartConfigByType = {
		monthlySales: getMonthlySalesConfig(normalizedCycle, {
			goals,
			baseline,
			data: normalizedData
		}),
		dailySales: getDailySalesConfig(normalizedCycle, {
			goals,
			baseline,
			data: normalizedData
		}),
		variluxMonthly: getVariluxMonthlyConfig(normalizedCycle, {
			baseline: variluxBaseline,
			goal: variluxGoal,
			data: normalizedData
		})
	};

	const config = chartConfigByType[type];

	if (!config) {
		return null;
	}

	const chartMaxValue = config.yAxisLeft.domain[1];

	const chartData = type === "monthlySales"
		? clampChartDataValues({
			data: normalizedData,
			dataKeys: ["value"],
			maxValue: chartMaxValue
		})
		: type === "dailySales"
			? clampDailySalesChartDataValues({
				data: normalizedData,
				maxValue: chartMaxValue
			})
			: normalizedData;

	const referenceLines = type === "dailySales"
		? [
			getCurrentDateReferenceLine(currentDate),
			...config.referenceLines
		]
		: config.referenceLines;

	const compactLayout = getCompactChartLayout({
		config,
		data: chartData,
		referenceLines,
		minGap: 24
	});

	const closedCycleLegendKey = closedCycleLegendKeyByType[type];

	const chartLegend =
		isClosedCycle && closedCycleLegendKey
			? config.legend.filter(item => item.key === closedCycleLegendKey)
			: config.legend;

	return (
		<>
			<LineChartGeneric
				{...config}
				{...compactLayout}
				legend={chartLegend}
				className={[
					"dashboard__line-chart",
					config.className,
					className
				].filter(Boolean).join(" ")}
			/>

			<p className="dashboard__scale-note">
				Escala vertical não linear, com espaçamento ampliado entre metas.
			</p>
		</>
	);
};

export default DashboardLineChart;